// THE ACTIVITY PICKER'S OPTIONS, INCLUDING FOR A VALUE THAT IS GONE.
//
// Owner on BA03, 2026-10-02: "schedule activity on shop drawing edit
// still show a default GUID and when i click inside the dropdown, no
// activity shows up."
//
// Both symptoms are one cause and neither is a regression to undo. He
// deleted 615 activities deliberately, which left ZERO live
// shop-drawing rows on that project - so the dropdown is correctly
// empty - and the picker builds its options ONLY from the activities the
// server offers while the form's value is the row's raw ganttItemId.
// Before deleted rows were filtered out, an orphaned value still matched
// something and rendered a label. Now nothing matches, and antd falls
// back to rendering the raw value: a GUID in a form field.
//
// THE FILTER STAYS. Offering activities the server refuses is the
// 62-offered-62-refused bug, which is worse than a bad label. The answer
// is to make the orphan SAY SO instead of showing its id.
//
// ONE DISABLED SENTINEL, and disabled on purpose: the value must still
// render as words, and must not be re-selectable once cleared.
import { activityFullPath } from "helpers/schedule/activityLabel";
import { leafOption, NOT_LEAF_NOTE } from "helpers/schedule/leafActivityOptions";
import { stepLabel } from "components/CoordinationGantt/ganttHelpers";

export const ORPHAN_LABEL = "This activity is no longer on the schedule - pick a new one";

// An empty-but-successful list needs its own words. A dropdown that opens
// onto nothing reads exactly like a broken one, and "No data" is what the
// form this replaced used to say. Distinct from the FAILED case, which
// has optionsError: "it is empty" and "we could not ask" are different
// facts and a person acts on them differently.
// AND IT NAMES THE ROUTE - in whichever of THREE ways is actually true.
//
// "Add them to the plan first" was true and useless: the owner read it,
// went to the schedule, looked at TYPE (task / milestone / meeting) and
// found no shop-drawing option, because the marker is STEP.
//
// Then he marked twelve rows and dated every one, and the picker was
// STILL empty - he had used the step `drawings_iff`, which was in the
// dropdown because that list had been written from a plan, while the
// picker matches `shop_drawings`. The message told him to add dates. The
// dates were there. Telling someone to fix what is already right is the
// same half-message as telling them to set a step without saying where.
//
// So the server says WHICH case it is (`emptyBecause`) and this turns it
// into the one sentence that is true.
export const EMPTY_NOTE = "No dated shop-drawing activities on this project's schedule yet. "
  + "On the schedule, select the activities, then Set… → Set step → Shop drawings, and give them a sign-off date.";

export function emptyActivityMessage(info) {
  if (!info || !info.reason) return EMPTY_NOTE;
  if (info.reason === "otherStep") {
    const named = (info.onOtherSteps || [])
      .map((x) => `${x.count} on ${stepLabel(x.step)}`)
      .join(", ");
    return "No activity on this project is on the Shop drawings step"
      + (named ? ` - ${named} instead. ` : ". ")
      + "A shop drawing hangs off that step specifically: select those activities on the "
      + "schedule, then Set… → Set step → Shop drawings.";
  }
  // 'noRows': the schedule itself is empty, so there is nothing to mark.
  return "This project's schedule has no activities yet. Add them on the schedule, "
    + "set their step to Shop drawings, and give them a sign-off date.";
}

// THE FULL PATH, NOT THE SHORTENED LABEL (owner, 2026-10-13: "show full path
// like we do on digest so user can understand which activity parent he is
// working in"). The option's label IS the digest's string, so the search box -
// optionFilterProp="label" - matches any part of it: "Level 1", "Area A",
// "K-Line" or the root all find the row.
//
// Every surface that picks a shop drawing's activity builds its options here:
// the Add drawer and the Edit dialog on the Shop Drawings page.
// THE LOWEST ACTIVITY ONLY, its path beneath (owner, 2026-10-15: "force all
// activities selection, to show only the lowest child ... below each leaf's
// child, it shows full path"). The server marks `leaf`; a row with work under
// it is not offered. Each option is the name on one line and the path above
// it on the next (helpers/schedule/leafActivityOptions), searchable by both.
export function activityOptions(activities, currentId, formatDate = null) {
  const list = [];
  const all = activities || [];
  for (const a of all) {
    if (a.leaf === false) continue;
    const full = activityFullPath(a);
    const o = leafOption({
      value: a.id,
      name: a.name || full,
      path: a.path || [],
      disabled: a.usable === false,
      note: a.usable === false ? (a.unusableReason || "not usable") : "",
      title: [
        full,
        a.areaName ? `Area ${a.areaName}` : a.area && `Area ${a.area}`,
        a.package,
        a.due && formatDate ? formatDate(a.due) : null,
      ].filter(Boolean).join(" · "),
    });
    list.push(o);
  }

  // The row points at something the server will not offer - deleted from
  // the schedule, or belonging to another trade - or at a row that now has
  // activities under it. Give the value a label so the field reads as a
  // sentence rather than an identifier. First in the list, because it is the
  // thing the person has to deal with.
  const id = currentId == null || currentId === "" ? "" : String(currentId);
  if (id && !list.some((o) => o.value === id)) {
    const parent = all.find((a) => String(a.id) === id);
    list.unshift(parent
      ? leafOption({ value: id, name: parent.name || activityFullPath(parent), path: parent.path || [], disabled: true, note: NOT_LEAF_NOTE })
      : { value: id, label: ORPHAN_LABEL, short: ORPHAN_LABEL, search: ORPHAN_LABEL, disabled: true, title: ORPHAN_LABEL });
  }
  return list;
}
